import { getNextStaticProps } from "@faustjs/next";
import { client, PageIdType } from "client";
import { GetStaticPropsContext } from "next";
import React from "react";
import {
  useFeaturedPost,
  usePaginatedPosts,
  useMenu,
  useSubscribeToNewsletter,
  useGeneralOptionsSettings,
} from "hooks";
import {
  BlogFeaturedPost,
  BlogPostCard,
  BlogCategories,
  NewsletterCTA,
  CEOLink,
  GenericDivider,
} from "components";
import Layout from "layouts/main";
import _isEqual from "lodash/isEqual";
import _isEmpty from "lodash/isEmpty";

export default function Page() {
  const options = useGeneralOptionsSettings();
  const ceoLink = options?.generalSettings?.ceoLink;
  const { useQuery } = client;
  const { usePage } = client;
  const categories = useQuery().categories().nodes;
  const featuredPost = useFeaturedPost();
  const POSTS_PER_PAGE = 6;
  const { data, fetchMore, isLoading } = usePaginatedPosts(POSTS_PER_PAGE);
  const headerLinks = useMenu("PRIMARY");
  const footerLinks = useMenu("FOOTER");

  const page = usePage({
    id: `/blog`,
    idType: PageIdType.URI,
  });

  const { isSubscribed, subscribeOnSubmit, message, isError } =
    useSubscribeToNewsletter();

  return (
    <Layout page={page} headerLinks={headerLinks} footerLinks={footerLinks}>
      <section className="section-container w-full mx-auto mt-16">
        <div className="content-container w-full px-6 md:px-16 lg:px-20 xl:px-12 mx-auto">
          <h1 className="font-bold text-4xl text-black mb-12">Blog</h1>
          <div className="flex justify-start items-start w-full">
            <div className="flex flex-col justify-start items-start w-full lg:w-2/3">
              <BlogFeaturedPost post={featuredPost} />
              <div className="md:hidden w-full">
                <NewsletterCTA
                  isSubscribed={isSubscribed}
                  subscribeOnSubmit={subscribeOnSubmit}
                  message={message}
                  isError={isError}
                />
                <GenericDivider className="my-8" />
                <BlogCategories categories={categories} activeCategory={""} />
              </div>
              {data?.nodes.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}

              {data?.hasNextPage && data?.endCursor ? (
                <button
                  className="font-bold text-white text-sm bg-black rounded-lg px-5 py-3"
                  disabled={isLoading}
                  onClick={() => {
                    fetchMore({
                      where: { onlySticky: false },
                      first: POSTS_PER_PAGE,
                      after: data?.endCursor,
                    });
                  }}
                >
                  {isLoading && <p>Loading...</p>}
                  {!isLoading && <span>Load More</span>}
                </button>
              ) : null}
            </div>
            <aside className="hidden md:block w-1/3 sticky">
              <BlogCategories categories={categories} activeCategory={""} />
              <div className="mt-8 md:pl-16">
                <NewsletterCTA
                  isSubscribed={isSubscribed}
                  subscribeOnSubmit={subscribeOnSubmit}
                  message={message}
                  isError={isError}
                />
              </div>
            </aside>
          </div>
        </div>
        <div className="flex justify-start items-start w-full pt-16">
          <div className="bg-blue w-1/4 h-1.5"></div>
          <div className="bg-green w-1/4 h-1.5"></div>
          <div className="bg-red w-1/4 h-1.5"></div>
          <div className="bg-yellow w-1/4 h-1.5"></div>
        </div>
      </section>
      <CEOLink
        className="h-[70px] w-[271px] fixed bottom-5 right-5 z-50"
        link={ceoLink}
      />
    </Layout>
  );
}

export async function getStaticProps(context: GetStaticPropsContext) {
  return getNextStaticProps(context, {
    Page,
    client,
  });
}
